import type { Locale } from "../shared/project-state";

export const dictionaries = {
  "zh-CN": {
    navBlueprint: "蓝图",
    navMission: "驾驶舱",
    navTau: "韬定律",
    navCapsules: "胶囊",
    navSources: "源观察",
    navFeatures: "能力",
    navLogin: "登录",
    heroEyebrow: "Billow AIOS · Local-first AI Operating System",
    heroTitle: "AIOS v1.3：用韬（τ）定律，把想法压缩成可信能力。",
    heroBody:
      "AIOS 不是无限堆代码，而是 local-first 的可信能力编排与生成系统：用 Capability Capsule 接入外部能力，用 Evidence / Reviewer / Rollback 守住质量。",
    primaryCta: "查看项目驾驶舱",
    secondaryCta: "理解 L0-L5 蓝图",
    live: "实时",
    stale: "需刷新",
    fallback: "本地快照",
    demo: "演示",
    overall: "总体进度",
    currentStage: "当前阶段",
    tasks: "任务",
    difficulty: "难度",
    lastUpdated: "最后更新",
    sourceSnapshot: "状态源快照",
    sourceAge: "源年龄",
    refresh: "刷新",
    googleLogin: "使用 Google 登录",
    logout: "退出",
    signedOut: "未登录",
    signedIn: "已登录",
    admin: "管理员",
    readOnly: "只读驾驶舱",
    blueprintMap: "L0-L5 蓝图地图",
    nextTasks: "后续任务",
    tauLaw: "韬（τ）定律链路",
    tauBody: "压缩等待、重复理解、黑盒排障和无效返工；不压缩证据、安全、回滚和用户控制权。",
    capabilityCapsules: "Capability Capsule 能力胶囊",
    capsuleBody:
      "外部能力先进入 C0/C1 的只读候选轨道，只有通过 Spec、Evidence、Reviewer 与 rollback 后才可能晋级。",
    sourceFindings: "开源能力观察",
    sourceFindingsBody:
      "官网只展示已进入 Evidence 的只读发现：帮助理解 AIOS 如何借力开源，但不授予采用、安装、执行或激活权限。",
    sourceRequests: "请求",
    sourceBytes: "字节",
    sourceDirectory: "目录",
    sourceNextGate: "下一 Gate",
    sourceBoundary: "边界",
    longGateGuard: "反黑洞 Gate",
    authorityBoundary: "只读观察边界",
    notFormalAuthority: "官网状态是 observational，不替代正式 Task Spec / Gate / Evidence / Reviewer。",
    progressChart: "进度图表",
    currentTasks: "当前与近期任务",
    riskControl: "边界与风险",
    themeSuite: "主题套装",
    colorMode: "界面颜色",
    locale: "语言",
    neural: "Neural OS",
    forge: "Forge Lab",
    atlas: "Atlas Map",
    light: "白",
    dark: "暗",
    parchment: "羊皮卷",
  },
  "en-US": {
    navBlueprint: "Blueprint",
    navMission: "Mission",
    navTau: "Tau",
    navCapsules: "Capsules",
    navSources: "Sources",
    navFeatures: "Features",
    navLogin: "Login",
    heroEyebrow: "Billow AIOS · Local-first AI Operating System",
    heroTitle: "AIOS v1.3: compress ideas into trusted capabilities with the tau law.",
    heroBody:
      "AIOS is not infinite hand-written code. It is a local-first capability orchestration and generation system: Capability Capsules borrow strength while Evidence, Reviewer and Rollback preserve quality.",
    primaryCta: "Open Mission Control",
    secondaryCta: "Understand L0-L5",
    live: "Live",
    stale: "Stale",
    fallback: "Local snapshot",
    demo: "Demo",
    overall: "Overall progress",
    currentStage: "Current stage",
    tasks: "Tasks",
    difficulty: "Difficulty",
    lastUpdated: "Last updated",
    sourceSnapshot: "Source snapshot",
    sourceAge: "Source age",
    refresh: "Refresh",
    googleLogin: "Sign in with Google",
    logout: "Logout",
    signedOut: "Signed out",
    signedIn: "Signed in",
    admin: "Admin",
    readOnly: "Read-only mission control",
    blueprintMap: "L0-L5 blueprint map",
    nextTasks: "Next tasks",
    tauLaw: "Tau law chain",
    tauBody:
      "Compress waiting, repeated context, black-box debugging and rework; do not compress evidence, safety, rollback or user control.",
    capabilityCapsules: "Capability Capsules",
    capsuleBody:
      "External capabilities first enter the C0/C1 read-only candidate track and can only mature through Spec, Evidence, Reviewer and rollback.",
    sourceFindings: "Open-source capability observations",
    sourceFindingsBody:
      "The site only surfaces read-only findings already backed by Evidence: they explain how AIOS borrows strength from open source without granting adoption, install, execution or activation.",
    sourceRequests: "Requests",
    sourceBytes: "Bytes",
    sourceDirectory: "Directory",
    sourceNextGate: "Next Gate",
    sourceBoundary: "Boundary",
    longGateGuard: "Anti-black-hole Gates",
    authorityBoundary: "Read-only authority boundary",
    notFormalAuthority:
      "Website status is observational; it does not replace formal Task Spec / Gate / Evidence / Reviewer.",
    progressChart: "Progress chart",
    currentTasks: "Current and upcoming tasks",
    riskControl: "Boundaries and risks",
    themeSuite: "Visual suite",
    colorMode: "Color mode",
    locale: "Language",
    neural: "Neural OS",
    forge: "Forge Lab",
    atlas: "Atlas Map",
    light: "Light",
    dark: "Dark",
    parchment: "Parchment",
  },
} satisfies Record<Locale, Record<string, string>>;

export function t(locale: Locale, key: keyof (typeof dictionaries)["zh-CN"]) {
  return dictionaries[locale][key];
}
